<template>
  <v-row justify="center" style="z-index: 999">
    <v-dialog
      v-model="dialog"
      persistent
      max-width="500px"
    >
      <v-card>
        <v-toolbar
        elevation="4"
        class="mb-4"
        >
            <v-toolbar-title>
                <span class="text-h5" data-testid="contacts-details-title">{{ $t('contacts.detailsDialog.title') }}</span>
            </v-toolbar-title>
            <v-spacer></v-spacer>
            <v-toolbar-items>
              <v-btn icon v-if="canContactsWrite || canContactsManagePersonal" @click="openContactUpdateForm()" data-testid="contacts-details-update-btn">
                <v-icon>mdi-pencil</v-icon>
              </v-btn>
              <v-btn icon v-if="contact && (canContactsWrite || canContactsManagePersonal)" @click="openDeleteDialog(contact)" data-testid="contacts-details-delete-btn">
                <v-icon>mdi-delete</v-icon>
              </v-btn>
              <v-btn icon @click="close()" data-testid="contacts-details-close-btn">
                <v-icon>mdi-close</v-icon>
              </v-btn>
            </v-toolbar-items>
        </v-toolbar>
        <v-card-text>
          <div v-if="contact">
            <v-list>
              <v-list-item>
                <v-list-item-avatar size="70">
                  <v-img v-if="contact.hasImage" :src="contact.imageUrl" :alt="contact.firstName"></v-img>
                  <v-img v-else src="/default_picture_profile.png" :alt="contact.firstName" data-testid="contacts-details-defaultPicture"></v-img>
                </v-list-item-avatar>

                <v-list-item-content class="ml-4">
                  <v-list-item-title class="mb-2"><h3 data-testid="contacts-details-name">{{contact.firstName}} {{contact.lastName}}</h3></v-list-item-title><br/>
                  <v-list-item-subtitle v-if="contact.company" data-testid="contacts-details-company">
                    <span><v-icon small color="grey">mdi-domain</v-icon> {{ contact.company }}</span>
                  </v-list-item-subtitle>
                  <v-list-item-subtitle v-if="contact.jobTitle" data-testid="contacts-details-job">
                    <span><v-icon small color="grey">mdi-account-tie</v-icon> {{ contact.jobTitle }}</span>
                  </v-list-item-subtitle>
                </v-list-item-content>
              </v-list-item>
            </v-list>
            <div class="mt-4 mb-4" v-if="contact.phoneNumbers">
              <v-divider class="mb-4"></v-divider>
              <p v-for="(phoneNumber, numberPosition) in contact.phoneNumbers" v-bind:key="numberPosition" class="ml-4" data-testid="contacts-details-phoneNumbers">
                <span :data-testid="`contacts-details-phoneNumber-${numberPosition}`">
                  <v-icon small class="mr-2">mdi-phone</v-icon>
                  <b>{{ phoneNumber.label }}</b>:
                  <v-btn
                    color="primary" outlined small
                    @click="!showTelURLs ? onClickCall(phoneNumber) : null"
                    :href="showTelURLs ? `tel:${phoneNumber.phone_number}` : null"
                  >
                    {{ phoneNumber.phone_number }}{{ phoneNumber.extension ? ` (${phoneNumber.extension})` : '' }}
                    <v-icon right dark>mdi-phone-dial</v-icon>
                  </v-btn>
                </span>
              </p>
            </div>
            <div class="mt-4 mb-4" v-if="contact.emails.length > 0">
              <v-divider class="mb-4"></v-divider>
              <span v-for="(email, emailPosition) in contact.emails" v-bind:key="emailPosition" class="ml-4" data-testid="contacts-details-emails">
                <span :data-testid="`contacts-details-email-${emailPosition}`">
                  <v-icon small class="mr-2">mdi-email</v-icon><b>{{ email.label }}</b>: <a :href="`mailto:${email.email}`">{{ email.email }}</a> <br/>
                </span>
              </span>
            </div>
            <div class="mt-4 mb-4" v-if="contact.street || contact.city || contact.state || contact.country">
              <v-divider class="mb-4"></v-divider>
              <span class="ml-4">
                <v-icon small class="mr-2">mdi-map-marker</v-icon><b>{{ contact.addressType }}</b>:&nbsp;
                <span data-testid="contacts-details-address">
                  <span v-if="contact.street">{{ contact.street }}, </span>
                  <span v-if="contact.city">{{ contact.city }}, </span>
                  <span v-if="contact.state">{{ contact.state }}, </span>
                  <span v-if="contact.country">{{ contact.country }} </span>
                </span>
                <br/>
              </span>
              <span class="ml-4">
                <v-icon small class="mr-2">mdi-map-search</v-icon>
                <a :href="`https://www.google.com/maps/search/?api=1&query=${contact.address()}`">{{ $t('contacts.detailsDialog.openMap') }}</a>
              </span>
            </div>
            <div v-if="!isPersonal && contact.tagsAsArray.length > 0">
              <v-divider class="mb-4"></v-divider>
              <v-card-title>{{ $t('contacts.columns.tags') }}</v-card-title>
              <v-chip
                v-for="(tag, tagPosition) in contact.tagsAsArray" v-bind:key="tagPosition"
                data-testid="contacts-details-tags"
                class="ma-1 ml-4"
              >
                <span>{{ tag }}</span>
              </v-chip>
            </div>
            <div v-if="isPersonal && contact.personalTagsAsArray.length > 0">
              <v-card-title>{{ $t('contacts.columns.personalTags') }}</v-card-title>
              <v-chip
                v-for="(tag, tagPosition) in contact.personalTagsAsArray" v-bind:key="tagPosition"
                data-testid="contacts-details-personalTags"
                class="ma-1 ml-4"
              >
                <span>{{ tag }}</span>
              </v-chip>
            </div>
          </div>
        </v-card-text>
        <v-card-actions>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-row>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
export default {
  name: 'contacts-DetailsDialog',
  computed: {
    ...mapGetters({
      dialog: 'contacts/detailsDialog/dialog',
      contact: 'contacts/detailsDialog/contact',
      tags: 'contacts/tags/get',
      personalTags: 'contacts/personalTags/get',
      isPersonal: 'contacts/detailsDialog/isPersonal',
      canContactsWrite: 'permissions/contactsWrite',
      canContactsManagePersonal: 'permissions/contactsManagePersonal',
      showTelURLs: 'showTelURLs'
    })
  },
  methods: {
    ...mapActions({
      close: 'contacts/detailsDialog/close',
      openDeleteDialog: 'contacts/deleteForm/open',
      openContactForm: 'contacts/form/open',
      callNumber: 'callNumber'
    }),

    onClickCall (phoneNumber) {
      if (!phoneNumber) return null
      let number = phoneNumber.phone_number
      // Some cases we don't have a phoneNumber but an extension?
      if (!number || number?.length === 0) {
        number = phoneNumber.extension
      }
      this.callNumber({ number: number })
    },

    openContactUpdateForm () {
      const isPersonal = this.isPersonal
      const isUpdate = true
      const contact = this.contact
      this.openContactForm({ contact, isPersonal, isUpdate })
    }
  }
}
</script>

<style>
</style>
